/**
 * ADR-0055 / V101-8-5: in-network (customer-run) collectors on the Setup page.
 * List: any member. Add / reinstall / revoke: owner or admin (the server enforces).
 * An install code is shown once, inside the install command, next to the collector it belongs to.
 *
 * V105-7-2 critic B1: "Add a verification runner" registers a manual verification runner (purpose
 * `verification_runner`, ADR-0074 §3) through the same owner / admin registration path; its code is
 * shown once inside the `docker run … register` command. The runner posts verification results only.
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { getConnectorManifest } from '@aigovops/core';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Spinner, StatusGlyph, type StatusGlyphKind } from '@aigovops/design-system';
import {
  ApiError, createCustomerCollector, listCustomerCollectors, revokeCustomerCollector, rotateCustomerCollector, type CustomerCollector,
} from '../lib/api';
import { AskAdmin } from './AskAdmin';
import { runnerRelease } from '../lib/runner-release';
import { relativeTime } from './PostureConnections';
import './posture-connections.css';

/** Chart the collector release publishes (packages/collector-agent/chart). */
export const COLLECTOR_CHART = 'oci://ghcr.io/channelmatter/charts/gacpro-collector';
const NS = 'gacpro';
/** Kinds a collector can check (manifest runnableIn: customer). */
const TARGET_KINDS = ['qdrant', 'weaviate', 'ollama', 'vllm', 'kubernetes'];
const POLL_MS = 10_000;
/** Server-side install-code lifetime (REGISTRATION_CODE_TTL_MS). */
const CODE_TTL_MS = 15 * 60_000;

type Ref = Pick<CustomerCollector, 'name' | 'collector_id'>;

/** One Helm release per collector (name + id suffix), so two collectors never share a release. */
export function releaseName(c: Ref): string {
  const slug = c.name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30).replace(/-+$/, '');
  return `gacpro-${slug || 'collector'}-${c.collector_id.slice(-4).toLowerCase()}`;
}

export function valuesCommand(): string {
  return `helm show values ${COLLECTOR_CHART} > values.yaml`;
}

/** First install reads the edited values file; a reinstall reuses the installed values and swaps the key. */
export function installCommand(code: string, apiUrl: string, c: Ref, reinstall = false): string {
  return [
    `helm upgrade --install ${releaseName(c)} ${COLLECTOR_CHART} \\`,
    `  --namespace ${NS} --create-namespace \\`,
    reinstall ? '  --reuse-values \\' : '  -f values.yaml \\',
    `  --set api.url=${apiUrl} \\`,
    `  --set registration.code=${code}`,
  ].join('\n');
}

/**
 * The manual verification runner image (packages/gac-action/Dockerfile.runner), pinned by the digest
 * published in release/gacpro-trust.json (V105-7-2 round 2: never a placeholder in copied text).
 */
/** One-time registration of a manual verification runner (the private key stays in the volume). */
export function runnerRegisterCommand(code: string, apiUrl: string, imageRef: string): string {
  return [
    'docker run --rm -v gacpro-runner:/data \\',
    `  -e GACPRO_REGISTRATION_CODE=${code} -e GACPRO_BASE_URL=${apiUrl} \\`,
    `  ${imageRef} register`,
  ].join('\n');
}
/** Run a downloaded bundle with the registered runner (sandbox for feasibility checks). */
export function runnerVerifyCommand(o: { bundleFile: string; environment: 'sandbox' | 'target'; signerIdentity: string; signerIssuer: string; imageRef: string }): string {
  return [
    'docker run --rm -v gacpro-runner:/data -v "$PWD":/in:ro \\',
    `  ${o.imageRef} verify --bundle-file /in/${o.bundleFile} --environment ${o.environment} \\`,
    `  --signer-identity ${o.signerIdentity} --signer-issuer ${o.signerIssuer}`,
  ].join('\n');
}

export const upgradeCommand = (c: Ref) => `helm upgrade ${releaseName(c)} ${COLLECTOR_CHART} --namespace ${NS} --reuse-values`;
export const uninstallCommand = (c: Ref) => `helm uninstall ${releaseName(c)} --namespace ${NS}`;
export const logsCommand = (c: Ref) => `kubectl --namespace ${NS} logs deploy/${releaseName(c)} --tail=50`;
/** Change what it checks, step 1: save the installed values (YAML, no header). */
export const saveValuesCommand = (c: Ref) => `helm get values ${releaseName(c)} --namespace ${NS} -o yaml > values.yaml`;
/** Step 2, after editing `targets`: apply. */
export const applyValuesCommand = (c: Ref) => `helm upgrade ${releaseName(c)} ${COLLECTOR_CHART} --namespace ${NS} --reuse-values -f values.yaml`;

const STATUS: Record<CustomerCollector['status'], { label: string; tone: string; glyph: StatusGlyphKind }> = {
  awaiting_registration: { label: 'Not installed yet', tone: 'neutral', glyph: 'clock' },
  registered: { label: 'Waiting for first report', tone: 'neutral', glyph: 'clock' },
  reporting: { label: 'Reporting', tone: 'success', glyph: 'check' },
  silent: { label: 'Not reporting', tone: 'warning', glyph: 'warning' },
  revoked: { label: 'Revoked', tone: 'neutral', glyph: 'x' },
};

const kindLabel = (k: string) => (getConnectorManifest(k)?.ui.label ?? k).replace(/\s*\(customer-run\)$/i, '');
const cadenceLabel = (s: number | null) => (!s ? null : s % 86400 === 0 ? (s === 86400 ? 'daily' : `every ${s / 86400} days`) : s % 3600 === 0 ? (s === 3600 ? 'hourly' : `every ${s / 3600} hours`) : `every ${Math.round(s / 60)} min`);

type Purpose = 'collector' | 'verification_runner';
const isRunner = (c: Pick<CustomerCollector, 'purpose'>) => c.purpose === 'verification_runner';

type Issued = { collector_id: string; code: string; expires_at: string; issued_at: number; reinstall: boolean };

/** Copy, or — when the clipboard is blocked — select the text so Ctrl+C works. */
function useCopy() {
  const [msg, setMsg] = useState('');
  const copy = async (text: string, target: HTMLElement | null, what: string) => {
    setMsg('');
    try { await navigator.clipboard.writeText(text); window.setTimeout(() => setMsg(`${what} copied.`), 50); }
    catch {
      if (target) {
        const range = document.createRange();
        range.selectNodeContents(target);
        const sel = window.getSelection();
        sel?.removeAllRanges(); sel?.addRange(range);
        target.focus();
      }
      window.setTimeout(() => setMsg(`${what} selected — press Ctrl+C (⌘C on Mac) to copy.`), 50);
    }
  };
  return { msg, copy };
}

export function CommandBlock({ label, text, primary, copyLabel, note }: { label: string; text: string; primary?: boolean; copyLabel: string; note?: ReactNode }) {
  const pre = useRef<HTMLPreElement>(null);
  const { msg, copy } = useCopy();
  const noteId = useId();
  const describedBy = note ? noteId : undefined;
  return <div className="v108-cmd-block">
    <pre ref={pre} className="v96-code v108-cmd" tabIndex={0} role="region" aria-label={label} aria-describedby={describedBy}>{text}</pre>
    <div className="v75-actions">
      <Button variant={primary ? 'primary' : 'secondary'} aria-describedby={describedBy} onClick={() => void copy(text, pre.current, label)}>{copyLabel}<span className="sr-only">: {label}</span></Button>
      {note && <span id={noteId} className="v75-muted">{note}</span>}
      <span role="status" className="v75-muted">{msg}</span>
    </div>
  </div>;
}

/** A disclosure holding one copyable command. */
function CommandDisclosure({ summary, label, text }: { summary: string; label: string; text: string }) {
  return <details className="v96-scopes"><summary>{summary}</summary><CommandBlock label={label} text={text} copyLabel="Copy command" /></details>;
}

/** Save → edit → apply, each command copied on its own so a paste can't skip the edit. */
function TargetsSteps({ c }: { c: Ref }) {
  return <ol className="v96-steps v108-steps">
    <li>Save its current settings.
      <CommandBlock label={`Command that saves ${c.name}'s settings`} text={saveValuesCommand(c)} copyLabel="Copy command" />
    </li>
    <li>In values.yaml, list what to check under <code>targets</code> ({TARGET_KINDS.map(kindLabel).join(', ')}).</li>
    <li>Apply it.
      <CommandBlock label={`Command that applies ${c.name}'s settings`} text={applyValuesCommand(c)} copyLabel="Copy command" />
    </li>
  </ol>;
}

export function CustomerCollectors({ slug, editable }: { slug: string; editable: boolean }) {
  const [rows, setRows] = useState<CustomerCollector[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adding, setAdding] = useState<Purpose | false>(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [revokedNotice, setRevokedNotice] = useState<CustomerCollector | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | undefined>();
  const [live, setLive] = useState('');
  const addButton = useRef<HTMLButtonElement>(null);
  const addRunnerButton = useRef<HTMLButtonElement>(null);
  const revokedToggle = useRef<HTMLElement>(null);
  const revokedNoticeRef = useRef<HTMLDivElement>(null);
  const focusAfter = useRef<string | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoadError(null);
    try { setRows((await listCustomerCollectors(slug)).collectors); }
    catch (e) { if (!quiet) setLoadError(e instanceof Error ? e.message : 'Collectors could not load.'); }
  }, [slug]);
  useEffect(() => { void load(); }, [load]);

  // Poll while an install is pending, so the row turns to Reporting without a reload.
  const waiting = !!issued || (rows ?? []).some(r => (r.status === 'awaiting_registration' && !!r.code_expires_at) || r.status === 'registered');
  useEffect(() => {
    if (!waiting) return;
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') void load(true); }, POLL_MS);
    return () => window.clearInterval(t);
  }, [waiting, load]);

  // Move focus after a row re-renders (Done → the row's name; revoke → the Revoked toggle).
  useEffect(() => {
    const target = focusAfter.current;
    if (!target) return;
    focusAfter.current = null;
    const el = target === 'revoked' ? (revokedNoticeRef.current ?? revokedToggle.current)
      : target === 'add' ? (addButton.current ?? revokedToggle.current)
      : document.getElementById(`collector-${target}`);
    el?.focus();
  });

  const act = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setActionError(null);
    try { await fn(); } catch (e) { setActionError(e instanceof ApiError || e instanceof Error ? e.message : 'That did not work. Try again.'); }
    finally { setBusy(null); }
  };

  const reinstall = (c: CustomerCollector) => act(`rotate:${c.collector_id}`, async () => {
    const r = await rotateCustomerCollector(slug, c.collector_id);
    setConfirmRevoke(null);
    setIssued({ collector_id: c.collector_id, code: r.registration_code, expires_at: r.expires_at, issued_at: Date.parse(r.expires_at) - CODE_TTL_MS, reinstall: c.status !== 'awaiting_registration' });
  });

  const create = async (name: string, purpose: Purpose) => {
    setBusy('create'); setActionError(null); setNameError(undefined);
    try {
      const r = purpose === 'verification_runner' ? await createCustomerCollector(slug, name, purpose) : await createCustomerCollector(slug, name);
      setRows(prev => [...(prev ?? []), r.collector]);
      setAdding(false);
      setRevokedNotice(null); // the replacement exists; the uninstall command stays under Revoked
      setIssued({ collector_id: r.collector.collector_id, code: r.registration_code, expires_at: r.expires_at, issued_at: Date.parse(r.expires_at) - CODE_TTL_MS, reinstall: false });
    } catch (e) {
      // A taken name belongs on the field, not in a banner.
      if (e instanceof ApiError && e.status === 409) setNameError(`${purpose === 'verification_runner' ? 'A runner or collector' : 'A collector'} already has that name. Choose another.`);
      else setActionError(e instanceof Error ? e.message : 'That did not work. Try again.');
    } finally { setBusy(null); }
  };

  const revoke = (c: CustomerCollector) => act(`revoke:${c.collector_id}`, async () => {
    const r = await revokeCustomerCollector(slug, c.collector_id);
    setRows(prev => (prev ?? []).map(x => (x.collector_id === c.collector_id ? { ...x, ...r.collector } : x)));
    setConfirmRevoke(null);
    if (issued?.collector_id === c.collector_id) setIssued(null);
    const neverInstalled = c.status === 'awaiting_registration';
    setRevokedNotice(neverInstalled ? null : c);
    focusAfter.current = 'revoked';
    setLive(''); window.setTimeout(() => setLive(neverInstalled ? `${c.name} deleted.` : `${c.name} revoked. It can no longer send results.`), 50);
  });

  const closePanel = (id: string) => { setIssued(null); focusAfter.current = id; void load(true); };

  const active = (rows ?? []).filter(r => r.status !== 'revoked');
  // Deleted before install: gone, not "revoked".
  const revoked = (rows ?? []).filter(r => r.status === 'revoked' && !!r.key_registered_at);
  const startAdd = (purpose: Purpose = 'collector') => { setAdding(purpose); setActionError(null); setNameError(undefined); };

  return <Card><section className="v75-panel" aria-labelledby="collectors-title">
    <div className="v75-page-heading">
      <div>
        <h2 id="collectors-title">In-network collectors</h2>
        <p className="v75-muted">Checks self-hosted AI — vector databases and model servers — from inside your network. It only sends results out.</p>
      </div>
      {editable && !adding && !issued && <div className="v75-actions">
        {active.length > 0 && <Button ref={addButton} variant="secondary" onClick={() => startAdd()}>Add a collector</Button>}
        <Button ref={addRunnerButton} variant="ghost" onClick={() => startAdd('verification_runner')}>Add a verification runner</Button>
      </div>}
    </div>
    <span role="status" className="sr-only">{live}</span>
    {actionError && <Alert variant="error"><p>{actionError}</p></Alert>}
    {revokedNotice && <div ref={revokedNoticeRef} tabIndex={-1} role="group" className="v108-inset v108-gap" aria-labelledby="collector-revoked-notice">
      <p id="collector-revoked-notice"><strong>{revokedNotice.name}</strong> is revoked. To replace it, save its settings, then remove it from your cluster:</p>
      {/* V104-C-6: Revoke → Create. Saved settings carry its targets into the replacement. */}
      <CommandBlock label={`Command that saves ${revokedNotice.name}'s settings`} text={saveValuesCommand(revokedNotice)} copyLabel="Copy command" />
      <CommandBlock label={`Uninstall command for ${revokedNotice.name}`} text={uninstallCommand(revokedNotice)} copyLabel="Copy command" />
      <div className="v75-actions">
        {!adding && <Button variant="secondary" onClick={() => startAdd()}>Add a replacement</Button>}
        <Button variant="ghost" onClick={() => { setRevokedNotice(null); focusAfter.current = 'add'; }}>Dismiss</Button>
      </div>
    </div>}
    {loadError ? <Alert variant="error"><p>{loadError}</p><Button onClick={() => void load()}>Retry</Button></Alert>
      : !rows ? <Spinner label="Loading collectors" />
      : <>
        {adding && <AddCollector purpose={adding} busy={busy === 'create'} serverError={nameError} onCancel={() => { const was = adding; setAdding(false); window.setTimeout(() => (was === 'verification_runner' ? addRunnerButton.current : addButton.current ?? addRunnerButton.current)?.focus(), 0); }} onCreate={(name) => void create(name, adding)} />}
        {active.length === 0 && !adding
          ? <EmptyState title="No in-network collectors"
            description={editable ? 'Add one to check AI that runs on your private network.' : 'An owner or admin can add one to check AI on your private network.'}
            action={editable ? <Button ref={addButton} variant="primary" onClick={() => startAdd()}>Add a collector</Button>
              : <AskAdmin slug={slug} what="adds in-network collectors" subject="In-network collectors in GAC Pro" compact />} />
          : <ul className="v96-sources">{active.map(c => <CollectorRow key={c.collector_id} c={c} slug={slug} editable={editable} busy={busy}
            issued={issued?.collector_id === c.collector_id ? issued : null}
            confirming={confirmRevoke === c.collector_id}
            onReinstall={() => void reinstall(c)}
            onAskRevoke={() => setConfirmRevoke(c.collector_id)}
            onCancelRevoke={() => setConfirmRevoke(null)}
            onRevoke={() => void revoke(c)}
            onClosePanel={() => closePanel(c.collector_id)} />)}</ul>}
        {revoked.length > 0 && <details className="v96-scopes"><summary ref={revokedToggle as React.Ref<HTMLElement>}>Revoked ({revoked.length})</summary>
          <ul className="v108-revoked">{revoked.map(c => <li key={c.collector_id}>
            <span>{c.name} · revoked {relativeTime(c.revoked_at)}</span>
            {editable && c.key_registered_at && <CommandDisclosure summary="Uninstall command" label={`Uninstall command for ${c.name}`} text={uninstallCommand(c)} />}
          </li>)}</ul>
        </details>}
      </>}
  </section></Card>;
}

function CollectorRow({ c, slug, editable, busy, issued, confirming, onReinstall, onAskRevoke, onCancelRevoke, onRevoke, onClosePanel }: {
  c: CustomerCollector; slug: string; editable: boolean; busy: string | null; issued: Issued | null; confirming: boolean;
  onReinstall: () => void; onAskRevoke: () => void; onCancelRevoke: () => void; onRevoke: () => void; onClosePanel: () => void;
}) {
  const codeExpired = c.status === 'awaiting_registration' && !c.code_expires_at;
  const s = codeExpired ? { label: 'Install code expired', tone: 'neutral', glyph: 'clock' as StatusGlyphKind } : STATUS[c.status];
  const revokeTrigger = useRef<HTMLButtonElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirming) cancel.current?.focus();
    else if (wasConfirming.current) revokeTrigger.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);
  const neverInstalled = c.status === 'awaiting_registration';
  const runner = isRunner(c);
  const noTargets = !runner && c.status === 'reporting' && c.kinds.length === 0;
  // States that need someone to act: members get whom to ask, admins get the action.
  const attention = neverInstalled || c.status === 'silent' || c.status === 'registered' || noTargets || c.out_of_date;
  const cadence = cadenceLabel(c.cadence_seconds);
  const nameSr = <span className="sr-only"> {c.name}</span>;
  const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCancelRevoke(); } };

  const reinstallButton = <Button variant="secondary" disabled={!!busy} onClick={onReinstall}>
    {busy === `rotate:${c.collector_id}` ? 'Issuing…' : runner ? 'New registration code' : neverInstalled ? 'New install code' : 'Reinstall'}{nameSr}
  </Button>;
  // V104-C-6 (delta review I-1): a new code leaves the old key working until it is redeemed,
  // so Reinstall is routine rotation and a suspected leak goes to Revoke → add a new one.
  const hintId = `collector-key-hint-${c.collector_id}`;
  const keyHint = !neverInstalled && <p id={hintId} className="v75-muted">Reinstall to rotate its key. If the key may have leaked, revoke this collector and add a new one.</p>;
  const revokeButton = <Button ref={revokeTrigger} variant="ghost" disabled={!!busy} onClick={onAskRevoke} aria-describedby={neverInstalled ? undefined : hintId}>{neverInstalled ? 'Delete' : 'Revoke'}{nameSr}</Button>;

  return <li className="v96-source">
    <div className="v96-source-head">
      <div className="v96-source-name">
        <h3 id={`collector-${c.collector_id}`} tabIndex={-1} className="v108-name">{c.name}</h3>
      </div>
      <Badge className={`v75-status ${s.tone}`}><StatusGlyph kind={s.glyph} /> {s.label}</Badge>
    </div>
    <dl className="v96-facts">
      <div><dt>Last report</dt><dd>{c.last_seen_at ? <>{relativeTime(c.last_seen_at)}{c.status === 'silent' && cadence ? ` · expected ${cadence}` : ''}</> : 'None yet'}</dd></div>
      <div><dt>Checks</dt><dd>{runner ? 'Verification checks you run with it' : c.kinds.length ? c.kinds.map(kindLabel).join(', ') : noTargets ? <span className="v108-warn"><StatusGlyph kind="warning" /> Nothing configured</span> : '—'}</dd></div>
      {c.out_of_date && <div><dt>Version</dt><dd><span className="v108-warn"><StatusGlyph kind="warning" /> {c.last_version} · update available</span></dd></div>}
    </dl>

    {issued ? <InstallPanel c={c} issued={issued} onNewCode={onReinstall} onClose={onClosePanel} busy={busy === `rotate:${c.collector_id}`} />
      : confirming ? <div className="v108-inset v108-danger" role="group" aria-labelledby={`revoke-q-${c.collector_id}`} aria-describedby={`revoke-d-${c.collector_id}`} onKeyDown={onEsc}>
        <p id={`revoke-q-${c.collector_id}`}><strong>{neverInstalled ? `Delete ${c.name}?` : `Revoke ${c.name}?`}</strong></p>
        <p id={`revoke-d-${c.collector_id}`}>{neverInstalled ? 'Its install code stops working.' : "It stops sending results immediately. This can't be undone."}</p>
        <div className="v75-actions">
          <Button ref={cancel} variant="secondary" onClick={onCancelRevoke}>Cancel</Button>
          <Button variant="danger" disabled={!!busy} onClick={onRevoke}>{busy === `revoke:${c.collector_id}` ? (neverInstalled ? 'Deleting…' : 'Revoking…') : neverInstalled ? 'Delete' : 'Revoke'}{nameSr}</Button>
        </div>
      </div>
      : editable ? <>
        {noTargets && <TargetsSteps c={c} />}
        {!runner && (c.status === 'silent' || c.status === 'registered') && <CommandDisclosure summary="Check it's running" label={`Log command for ${c.name}`} text={logsCommand(c)} />}
        {neverInstalled || (!runner && (c.status === 'silent' || c.status === 'registered'))
          ? <>{keyHint}<div className="v75-actions">{reinstallButton}{revokeButton}</div></>
          : <details className="v96-scopes"><summary>Manage<span className="sr-only"> {c.name}</span></summary>
            <div className="v108-manage">
              {c.out_of_date && <CommandBlock label={`Update command for ${c.name}`} text={upgradeCommand(c)} copyLabel="Copy update command" />}
              {!noTargets && <details className="v96-scopes"><summary>Change what it checks</summary><TargetsSteps c={c} /></details>}
              {keyHint}
              <div className="v75-actions">{reinstallButton}{revokeButton}</div>
            </div>
          </details>}
      </>
      : attention && <AskAdmin slug={slug} compact
        what={c.status === 'silent' ? `restarts or reinstalls ${c.name}` : noTargets ? `sets what ${c.name} checks` : c.out_of_date ? `updates ${c.name}` : `installs ${c.name}`}
        subject={`In-network collector ${c.name}`} />}
  </li>;
}

function InstallPanel({ c, issued, busy, onNewCode, onClose }: { c: CustomerCollector; issued: Issued; busy: boolean; onNewCode: () => void; onClose: () => void }) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 15_000); return () => window.clearInterval(t); }, []);
  const expiresMs = Date.parse(issued.expires_at);
  const minutes = Math.max(0, Math.ceil((expiresMs - now) / 60_000));
  const expired = expiresMs <= now;
  // Connected only once a key registered with THIS code and reported after it — rotating a
  // healthy collector must not jump to "reporting" on the old key's reports.
  const keyAt = c.key_registered_at ? Date.parse(c.key_registered_at) : NaN;
  const registered = keyAt >= issued.issued_at - 60_000;
  const connected = registered && !!c.last_seen_at && Date.parse(c.last_seen_at) >= keyAt;
  const clock = new Date(expiresMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const runner = isRunner(c);
  const release = runnerRelease();
  const title = runner ? `Register ${c.name}` : `${issued.reinstall ? 'Reinstall' : 'Install'} ${c.name}`;
  const phase = connected ? 'connected' : registered ? 'registered' : expired ? 'expired' : 'waiting';
  // Focus the panel heading on open and on every phase change; announce the change.
  // Focus the heading when the panel opens (new code); on a later phase change only if focus is
  // already in the panel (or lost) — a poll must not pull focus out of unrelated work.
  const panel = useRef<HTMLDivElement>(null);
  const lastCode = useRef<string | null>(null);
  useEffect(() => {
    const opened = lastCode.current !== issued.code;
    lastCode.current = issued.code;
    const active = document.activeElement;
    if (opened || !active || active === document.body || panel.current?.contains(active)) heading.current?.focus();
  }, [issued.code, phase]);
  const announce = phase === 'connected' ? `${c.name} is reporting.` : phase === 'registered' ? (runner ? `${c.name} is registered.` : `${c.name} is installed; waiting for its first report.`) : phase === 'expired' ? (runner ? 'The registration code expired.' : 'The install code expired.') : '';

  const status = <p role="status" className="sr-only">{announce}</p>;
  if (connected) {
    return <div ref={panel} className="v108-inset">
      {status}
      <h4 ref={heading} tabIndex={-1}><StatusGlyph kind="check" /> {runner ? `${c.name} is registered` : `${c.name} is reporting`}</h4>
      <p>{runner ? <>Registered. It posts a result each time you run a check with it.</> : c.kinds.length ? <>Checking {c.kinds.map(kindLabel).join(', ')}.</> : <>Connected, but nothing to check yet.</>}</p>
      {!runner && !c.kinds.length && <TargetsSteps c={c} />}
      <div className="v75-actions"><Button variant="primary" onClick={onClose}>Done</Button></div>
    </div>;
  }
  return <div ref={panel} className="v108-inset">
    {status}
    <h4 ref={heading} tabIndex={-1}>{title}</h4>
    {phase === 'registered' && runner ? <>
      <p className="v108-note"><StatusGlyph kind="check" /><span>Registered. It posts a result each time you run a check with it.</span></p>
      <div className="v75-actions"><Button variant="primary" onClick={onClose}>Done</Button></div>
    </> : phase === 'registered' ? <>
      <p className="v108-note"><StatusGlyph kind="check" /><span>Installed. Waiting for its first report.</span></p>
      <CommandDisclosure summary="Check it's running" label={`Log command for ${c.name}`} text={logsCommand(c)} />
      <div className="v75-actions"><Button variant="ghost" onClick={onClose}>Close</Button></div>
    </> : expired ? <>
      <p className="v108-note"><StatusGlyph kind="clock" /><span>{runner ? 'This registration code expired.' : 'This install code expired.'} Get a new one and run the command again.</span></p>
      <div className="v75-actions">
        <Button variant="primary" disabled={busy} onClick={onNewCode}>{busy ? 'Issuing…' : runner ? 'New registration code' : 'New install code'}</Button>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </> : runner ? <>
      <p>Run this once on the machine that will run your checks. Only its public key reaches GAC Pro.</p>
      {release?.imageRef ? <CommandBlock label={`Registration command for ${c.name}`} text={runnerRegisterCommand(issued.code, window.location.origin, release.imageRef)} primary copyLabel="Copy command"
        note={<>Works once · expires in {minutes} min ({clock}) · shown only now</>} /> : <RunnerNotPublished />}
      <div className="v108-foot">
        <p className="v108-note v75-muted"><StatusGlyph kind="clock" /><span>Waiting for {c.name} to register…</span></p>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </> : <>
      <p>{issued.reinstall
        ? 'Run this where you manage the cluster. It reuses the installed settings — if you removed the release, add -f values.yaml.'
        : 'Run this where you manage the cluster, next to your values.yaml.'}</p>
      <CommandBlock label={`Install command for ${c.name}`} text={installCommand(issued.code, window.location.origin, c, issued.reinstall)} primary copyLabel="Copy command"
        note={<><strong>Secret until used</strong> — keep it out of CI logs and tickets · works once · expires in {minutes} min ({clock}) · shown only now</>} />
      <div className="v108-foot">
        <p className="v108-note v75-muted"><StatusGlyph kind="clock" /><span>Waiting for {c.name} to report…</span></p>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </>}
  </div>;
}

/** Settings first (no code yet, so no clock running), then name → code. */
function AddCollector({ purpose, busy, serverError, onCreate, onCancel }: { purpose: Purpose; busy: boolean; serverError?: string; onCreate: (name: string) => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  useEffect(() => { if (serverError) setError(serverError); }, [serverError]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError(purpose === 'verification_runner' ? 'Name the runner, e.g. the machine it runs on.' : 'Name the collector, e.g. the cluster it runs in.'); return; }
    onCreate(name.trim());
  };
  const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } };
  if (purpose === 'verification_runner' && !runnerRelease()?.imageRef) {
    return <div className="v96-form v108-inset v108-gap" role="group" aria-label="Add a verification runner" onKeyDown={esc}>
      <RunnerNotPublished />
      <div className="v75-actions"><Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button></div>
    </div>;
  }
  if (purpose === 'verification_runner') {
    return <form className="v96-form v108-inset v108-gap" onSubmit={submit} noValidate onKeyDown={esc} aria-label="Add a verification runner">
      <p>A verification runner runs GAC Pro checks on a machine you choose (Docker) and posts the signed result. Use it when your CI can't run them.</p>
      <Field id="runner-name" label="Runner name" required hint="Where it runs, e.g. ops-build-host." error={error}>
        <Input autoFocus autoComplete="off" maxLength={100} value={name} onChange={e => { setName(e.currentTarget.value); setError(undefined); }} />
      </Field>
      <div className="v75-actions">
        <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Creating…' : 'Create registration code'}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>;
  }
  return <form className="v96-form v108-inset v108-gap" onSubmit={submit} noValidate onKeyDown={esc}>
    <ol className="v96-steps">
      <li>Save the default settings, then replace <code>targets: []</code> with what to check ({TARGET_KINDS.map(kindLabel).join(', ')}).
        <CommandBlock label="Command that saves the default settings" text={valuesCommand()} copyLabel="Copy command" />
      </li>
      <li>Name it to get the install command.
        <Field id="collector-name" label="Collector name" required hint="Usually the cluster it runs in, e.g. prod-us-east." error={error}>
          <Input autoFocus autoComplete="off" maxLength={100} value={name} onChange={e => { setName(e.currentTarget.value); setError(undefined); }} />
        </Field>
      </li>
    </ol>
    <div className="v75-actions">
      <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Creating…' : 'Create install code'}</Button>
      <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
    </div>
  </form>;
}

/** The runner image isn't in release/gacpro-trust.json yet: say so and offer support (no placeholder command). */
export function RunnerNotPublished() {
  return <p className="v108-note"><StatusGlyph kind="clock" /><span>The runner release isn't published yet.{' '}
    <a href={`mailto:support@gacpro.io?subject=${encodeURIComponent('GAC Pro runner release')}`}>Ask GAC Pro support</a></span></p>;
}
